import {createContext, PropsWithChildren, useMemo} from 'react';

import {useUserRoles} from '../../service/user-management/user-management-user';
import {useBrands} from '../brands/brand-hook';
import {useUser} from '../user/user-hook';

import {
    ADMIN_ROLE_IDS,
    BIG_BRAND_IDS,
    MOKKANO_BRAND_IDS,
    mokkanoReviewStatuses,
    WGIS_SUPPORT_USER_ID,
} from './personality-context-const';

export type PersonalityContextType = {
    userHasAdminRole: boolean;
    reviewStatusesMapping: Array<{
        statusCode: string;
        availableStatus: Array<string>;
    }> | null;
    isBigBrand: boolean;
    isWgisSupportUser: boolean;
};

export const PersonalityContext = createContext<PersonalityContextType>({
    userHasAdminRole: false,
    reviewStatusesMapping: null,
    isBigBrand: false,
    isWgisSupportUser: false,
});

export function PersonalityProvider(props: PropsWithChildren<Record<string, unknown>>): JSX.Element {
    const {children} = props;

    const {user} = useUser();
    const {data: ownUser} = useUserRoles();
    const {brands} = useBrands();

    const userHasAdminRole = useMemo(() => {
        return Boolean(ownUser?.role.pk && ADMIN_ROLE_IDS.includes(ownUser?.role.pk));
    }, [ownUser?.role.pk]);

    const reviewStatusesMapping = useMemo(() => {
        if (brands.some((brand) => MOKKANO_BRAND_IDS.includes(brand.id))) {
            return mokkanoReviewStatuses;
        }

        return null;
    }, [brands]);

    const isBigBrand = useMemo(() => {
        return brands.some((brand) => {
            return BIG_BRAND_IDS.includes(brand.id);
        });
    }, [brands]);

    const isWgisSupportUser = useMemo(() => {
        if (user?.id) {
            WGIS_SUPPORT_USER_ID.includes(user?.id);
        }

        return false;
    }, [user?.id]);

    const personalityProviderValue = useMemo(() => {
        return {
            userHasAdminRole,
            reviewStatusesMapping,
            isBigBrand,
            isWgisSupportUser,
        };
    }, [isBigBrand, isWgisSupportUser, reviewStatusesMapping, userHasAdminRole]);

    return <PersonalityContext.Provider value={personalityProviderValue}>{children}</PersonalityContext.Provider>;
}
